/*
 * Copyright (c) 2022. American Express Company
 */

// theirs
import React from 'react';
import { ButtonSecondary, IconSavings } from '@americanexpress/dls-react';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import LiteralsService from 'src/services/literals/LiteralsService';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Typography from 'src/components/typography/Typography';
import Card from 'src/components/atomic/atoms/card/Card';
import Divider from 'src/components/atomic/atoms/divider/Divider';
import Circle from 'src/components/atomic/atoms/circle/Circle';
import UtilsService from 'src/services/utils.service';
import { CardModel } from 'src/types/card.model';

// styles
import { SavingsGoalsViewModel } from 'src/types/view-models/savings-goals.view.model';
import bgColors from 'src/styles/bgColors.module.scss';
import styles from './savingsGoals.module.scss';
import vars from 'src/styles/variables.module.scss';
import GoalProgressBar from 'src/components/goal-progress-bar/GoalProgressBar';
import { savingGoalIcons } from 'src/bw/goal/Goal';
import { CardVariant } from 'src/types/card-variant.enum';
import Selectors from 'src/utils/selectors';

const Sections = (goTo: (...args: any[]) => any, savingsGoalsCards: CardModel[]): JSX.Element => {
    const sortedSavingsGoalsCards: CardModel[] = savingsGoalsCards.sort(
        (savingsGoalsCard1, savingsGoalsCard2): number => {
            return savingsGoalsCard1.endDate.compareTo(savingsGoalsCard2.endDate);
        }
    );

    return (
        <>
            {sortedSavingsGoalsCards.map((savingsGoalsCard, index): JSX.Element => {
                const purchaseName: string = savingsGoalsCard.attributes?.purchaseName;
                const title: string = purchaseName
                    ? LiteralsService.getLiteral('SavingsGoals.largePurchase', [purchaseName])
                    : UtilsService.capitalizeFirstLetterOfEachWord(savingsGoalsCard.description);
                const key: number = savingsGoalsCard.attributes?.linked_card_id || index;

                return (
                    <Card variant={CardVariant.SECONDARY} className={styles.section} key={`savingsSection-${key}`}>
                        <div className={styles.inline}>
                            <Circle diameter="48px" className={styles.icon}>
                                {savingGoalIcons[savingsGoalsCard.description]}
                            </Circle>
                            <Anchor
                                onClick={() => {
                                    goTo(`/card/${savingsGoalsCard.attributes?.linked_card_id}`);
                                }}
                                typographyProps={{ variant: TypographyVariants.BODY_BOLD }}
                            >
                                {title}
                            </Anchor>
                        </div>
                        <GoalProgressBar
                            currentAmount={savingsGoalsCard.amountSaved}
                            percent={savingsGoalsCard.percentSaved}
                            targetAmount={savingsGoalsCard.attributes?.targetBalance}
                        />
                    </Card>
                );
            })}
        </>
    );
};

export default function SavingsGoals({ goTo, savingsGoalsCards, ...props }: SavingsGoalsViewModel): JSX.Element {
    const onClick = (): void => goTo('/goals');
    return (
        <Card {...props}>
            <Typography component="h2" variant={TypographyVariants.HEADING_XS}>
                {LiteralsService.getLiteral('Insights.savingsGoals.yourSavingsGoals')}
            </Typography>
            <Divider />
            {Sections(goTo, savingsGoalsCards)}
            <Card className={bgColors.brandWeak}>
                <div className={styles.inline}>
                    <Circle diameter="48px" className={bgColors.brand}>
                        <IconSavings style={{ fill: vars.textLight }} />
                    </Circle>
                    <Typography component="h4" variant={TypographyVariants.BODY_BOLD}>
                        {LiteralsService.getLiteral('Insights.savingsGoals.addAnotherSavingsGoal')}
                    </Typography>
                </div>
                <Typography variant={TypographyVariants.SUBTEXT}>
                    {LiteralsService.getLiteral('Insights.savingsGoals.haveASavingsGoalInMind')}
                </Typography>
                <ButtonSecondary onClick={onClick} data-cy={Selectors.PlanAhead.INSIGHTS_SAVINGS_GOAL}>
                    {LiteralsService.getLiteral('Insights.savingsGoals.addLifeEvent')}
                </ButtonSecondary>
            </Card>
        </Card>
    );
}
